import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Chip, Paper, MenuItem, FormControl, InputLabel, Select,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import { Add, PlayArrow } from '@mui/icons-material';
import { formatMoney } from '../../utils/currency';
import {
  getPlans, createPlan, getMetrics, createMetric,
  getSubscriptions, getUsageSummary, generateInvoice,
  getInvoices, payInvoice, runBillingCycle, getSubscriptionSummary,
} from '../../services/subscriptionService';

function PlansTab() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: '', name: '', basePrice: 299, billingInterval: 'monthly', trialDays: '' as number | '',
    rideIncluded: 10, rideOverage: 25,
  });

  const { data, isLoading } = useQuery({ queryKey: ['sub-plans'], queryFn: getPlans });
  const createMutation = useMutation({
    mutationFn: () => createPlan({
      code: form.code,
      name: form.name,
      basePrice: form.basePrice,
      billingInterval: form.billingInterval,
      ...(form.trialDays === '' ? {} : { trialDays: form.trialDays }),
      metrics: [
        { metricCode: 'RIDES', includedQuantity: form.rideIncluded, overageRate: form.rideOverage },
        { metricCode: 'FOOD_ORDERS', includedQuantity: 5, overageRate: 15 },
      ],
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['sub-plans'] }); setOpen(false); },
  });

  const columns: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 100 },
    { field: 'name', headerName: 'Plan', flex: 1 },
    { field: 'billingInterval', headerName: 'Interval', width: 100 },
    { field: 'basePrice', headerName: 'Base ₹', width: 90 },
    { field: 'trialDays', headerName: 'Trial', width: 70 },
    {
      field: 'metrics', headerName: 'Metrics', width: 90,
      valueGetter: (_v, row) => (row.metrics || []).length,
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>New Plan</Button>
      </Box>
      <SerialNoDataGrid rows={data || []} columns={columns} loading={isLoading} getRowId={(r) => r._id} autoHeight
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }} />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Subscription Plan</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={6}><TextField fullWidth label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
            <Grid item xs={4}><TextField fullWidth type="number" label="Base Price" value={form.basePrice} onChange={(e) => setForm({ ...form, basePrice: Number(e.target.value) })} /></Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                type="number"
                label="Trial Days"
                value={form.trialDays}
                placeholder="Default"
                inputProps={{ min: 0, max: 365, step: 1 }}
                helperText="0–365 · blank = Default Trial (Site Settings)"
                onChange={(e) => setForm({ ...form, trialDays: e.target.value === '' ? '' : Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={4}><TextField fullWidth type="number" label="Rides Included" value={form.rideIncluded} onChange={(e) => setForm({ ...form, rideIncluded: Number(e.target.value) })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="number" label="Ride Overage ₹" value={form.rideOverage} onChange={(e) => setForm({ ...form, rideOverage: Number(e.target.value) })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => createMutation.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function MetricsTab() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', unit: 'unit', module: '' });

  const { data, isLoading } = useQuery({ queryKey: ['sub-metrics'], queryFn: getMetrics });
  const createMutation = useMutation({
    mutationFn: () => createMetric(form),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['sub-metrics'] }); setOpen(false); },
  });

  const columns: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 120 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'unit', headerName: 'Unit', width: 80 },
    { field: 'module', headerName: 'Module', width: 120 },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>New Metric</Button>
      </Box>
      <SerialNoDataGrid rows={data || []} columns={columns} loading={isLoading} getRowId={(r) => r._id} autoHeight
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }} />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Usage Metric</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={6}><TextField fullWidth label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Module" value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => createMutation.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SubscriptionsTab() {
  const queryClient = useQueryClient();
  const [usageSubId, setUsageSubId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['sub-subscriptions'], queryFn: () => getSubscriptions() });
  const { data: usage } = useQuery({
    queryKey: ['sub-usage', usageSubId],
    queryFn: () => getUsageSummary(usageSubId!),
    enabled: !!usageSubId,
  });

  const invoiceMutation = useMutation({
    mutationFn: (id: string) => generateInvoice(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sub-invoices'] }),
  });

  const columns: GridColDef[] = [
    { field: 'subscriptionNumber', headerName: '#', width: 140 },
    {
      field: 'userId', headerName: 'Customer', flex: 1,
      valueGetter: (_v, row) => row.userId ? `${row.userId.firstName} ${row.userId.lastName}` : '—',
    },
    {
      field: 'planId', headerName: 'Plan', width: 120,
      valueGetter: (_v, row) => row.planId?.name || '—',
    },
    {
      field: 'status', headerName: 'Status', width: 100,
      renderCell: (p) => (
        p.value === 'past_due' && p.row.graceEndsAt ? (
          <Chip
            label={`past due · until ${new Date(p.row.graceEndsAt).toLocaleDateString()}`}
            size="small"
            color="warning"
            title="Renewal payment failed — retried daily; cancelled if unpaid when the grace period ends"
          />
        ) : <Chip label={p.value as string} size="small" title={p.row.cancelReason === 'payment_failed' ? 'Cancelled: invoice unpaid after the grace period' : undefined} />
      ),
    },
    {
      field: 'actions', headerName: '', width: 200,
      renderCell: (p) => (
        <>
          <Button size="small" onClick={() => setUsageSubId(p.row._id)}>Usage</Button>
          <Button size="small" onClick={() => invoiceMutation.mutate(p.row._id)}>Invoice</Button>
        </>
      ),
    },
  ];

  return (
    <Box>
      <SerialNoDataGrid rows={data?.data || []} columns={columns} loading={isLoading} getRowId={(r) => r._id} autoHeight
        sx={{ bgcolor: 'background.paper', borderRadius: 2, mb: 2 }} />
      {usage && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Current Period Usage</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Estimated bill: {formatMoney(usage.estimatedTotal)} (base {formatMoney(usage.basePrice)} + overage)
          </Typography>
          {(usage.metrics || []).map((m: {
            metricCode: string; usedQuantity: number; includedQuantity: number;
            overageQuantity: number; estimatedOverageCharge: number;
          }) => (
            <Box key={m.metricCode} display="flex" justifyContent="space-between" py={0.5}>
              <Typography variant="body2">{m.metricCode}</Typography>
              <Typography variant="body2">
                {m.usedQuantity} / {m.includedQuantity} included
                {m.overageQuantity > 0 && ` · +${formatMoney(m.estimatedOverageCharge)} overage`}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}

function InvoicesTab() {
  const queryClient = useQueryClient();
  const billingMutation = useMutation({
    mutationFn: runBillingCycle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['sub-subscriptions'] });
    },
  });
  const payMutation = useMutation({
    mutationFn: payInvoice,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sub-invoices'] }),
  });

  const { data, isLoading } = useQuery({ queryKey: ['sub-invoices'], queryFn: () => getInvoices() });

  const columns: GridColDef[] = [
    { field: 'invoiceNumber', headerName: '#', width: 140 },
    {
      field: 'userId', headerName: 'Customer', width: 140,
      valueGetter: (_v, row) => row.userId?.firstName || '—',
    },
    { field: 'baseAmount', headerName: 'Base', width: 80, valueFormatter: (v) => `${formatMoney(v)}` },
    { field: 'usageAmount', headerName: 'Usage', width: 80, valueFormatter: (v) => `${formatMoney(v)}` },
    { field: 'totalAmount', headerName: 'Total', width: 90, valueFormatter: (v) => `${formatMoney(v)}` },
    {
      field: 'status', headerName: 'Status', width: 90,
      renderCell: (p) => <Chip label={p.value as string} size="small" />,
    },
    {
      field: 'actions', headerName: '', width: 90,
      renderCell: (p) =>
        p.row.status === 'open' || p.row.status === 'failed' ? (
          <Button size="small" onClick={() => payMutation.mutate(p.row._id)}>Pay</Button>
        ) : null,
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="outlined" startIcon={<PlayArrow />} onClick={() => billingMutation.mutate()}>
          Run Billing Cycle
        </Button>
      </Box>
      <SerialNoDataGrid rows={data?.data || []} columns={columns} loading={isLoading} getRowId={(r) => r._id} autoHeight
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }} />
    </Box>
  );
}

export default function SubscriptionsPage() {
  const [tab, setTab] = useState(0);
  const { data: summary } = useQuery({ queryKey: ['sub-summary'], queryFn: getSubscriptionSummary });

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Subscriptions & Usage Billing</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Base fee + metered overage · {summary?.activeSubscriptions ?? 0} active · MRR {formatMoney(summary?.monthlyRecurringRevenue ?? 0)}
      </Typography>

      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Active Subs', value: summary?.activeSubscriptions ?? 0 },
          { label: 'Open Invoices', value: summary?.openInvoices ?? 0 },
          { label: 'Usage Today', value: summary?.usageEventsToday ?? 0 },
        ].map((s) => (
          <Grid item xs={12} sm={4} key={s.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              <Typography variant="h4" fontWeight={700}>{s.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Plans" />
        <Tab label="Metrics" />
        <Tab label="Subscriptions" />
        <Tab label="Invoices" />
      </Tabs>
      {tab === 0 && <PlansTab />}
      {tab === 1 && <MetricsTab />}
      {tab === 2 && <SubscriptionsTab />}
      {tab === 3 && <InvoicesTab />}
    </Box>
  );
}
